import { useMemo, useState } from "react";
import { useTerm } from "@/lib/terminology.jsx";
import { formatDay } from "@/lib/format";
import { CheckCircle2, Clock, ChevronDown, ChevronRight, Loader2, AlertTriangle } from "lucide-react";
import { SkeletonRows } from "@/components/Skeleton";

/**
 * v5.13-r2 TaskList — Open + Completed sections.
 *
 * CW6.2.7: optional paginated mode. When `openTasks` / `completedTasks` are
 * passed (used by the Tasks page), the component shows server-provided
 * counts, an infinite-scroll sentinel on Open, and lazy-loads Completed on
 * expand. When only `tasks` is passed (used by Deal ▸ Tasks tab), the
 * component falls back to splitting locally.
 *
 * When `contextDealId` is provided (viewer is inside a specific deal's
 * Tasks tab), status/due-date come from the task's per-link fields for
 * that deal — never from the rollup.
 * Otherwise the rollup is used and a PARTIAL badge appears when only
 * some links are done.
 */
export default function TaskList({
  // Legacy single-list mode (DealTasksTab etc.)
  tasks,
  // CW6.2.7 paginated mode (Tasks page)
  openTasks,
  completedTasks,
  openCount,
  completedCount,
  openHasMore = false,
  openLoading = false,
  openError = null,
  onRetryOpen,
  sentinelRef,
  completedLoaded = true,
  completedLoading = false,
  completedHasMore = false,
  completedError = null,
  onExpandCompleted,
  onLoadMoreCompleted,
  // Common
  contextDealId = null,
  onOpen,
  onCompleteClick,
  onSnoozeClick,
  emptyText,
  showLinkedDeals = false,
}) {
  const term = useTerm();
  const [showCompleted, setShowCompleted] = useState(false);
  const paginated = !!openTasks;

  // Legacy path: split a single list by rollup
  const legacyPartitioned = useMemo(() => {
    if (paginated) return { open: [], completed: [] };
    const o = [];
    const c = [];
    for (const t of tasks || []) {
      const status = contextDealId
        ? t.context_status || "open"
        : t.rollup_status || t.status || "open";
      if (status === "completed") c.push(t);
      else o.push(t);
    }
    const dueOf = (t) => t.effective_due_date || t.due_date || "";
    o.sort((a, b) => dueOf(a).localeCompare(dueOf(b)));
    c.sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));
    return { open: o, completed: c };
  }, [tasks, contextDealId, paginated]);

  const open = paginated ? openTasks : legacyPartitioned.open;
  const completed = paginated ? completedTasks : legacyPartitioned.completed;
  const openN = paginated ? (openCount ?? open.length) : open.length;
  const completedN = paginated ? (completedCount ?? completed.length) : completed.length;

  // Empty state (legacy only — paginated mode shows "All caught up." inside Open)
  if (!paginated && !(tasks || []).length) {
    return (
      <div className="cw-card p-10 text-center text-sm text-[var(--cw-muted)]">
        {emptyText ||
          `No ${term("tasks").toLowerCase()} yet. Create one above to get started.`}
      </div>
    );
  }

  // Paginated mode: show skeleton while first batch is loading
  if (paginated && openLoading && open.length === 0 && !openError) {
    return (
      <div className="cw-card p-5" data-testid="tasks-skeleton">
        <SkeletonRows rows={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="cw-card overflow-hidden" data-testid="task-list-open">
        <div
          className="px-4 py-2 text-xs font-semibold uppercase tracking-widest border-b"
          style={{ borderColor: "var(--cw-border)", background: "rgba(40,42,116,0.03)" }}
        >
          Open ({openN})
        </div>
        {paginated && openError ? (
          <div className="p-6 text-sm text-center" data-testid="tasks-open-error">
            <div className="inline-flex items-center gap-1 text-[var(--cw-error)] mb-2">
              <AlertTriangle size={14} /> {openError}
            </div>
            {onRetryOpen && (
              <div>
                <button onClick={onRetryOpen}
                  className="cw-btn-ghost text-xs"
                  data-testid="tasks-open-retry">
                  Retry
                </button>
              </div>
            )}
          </div>
        ) : open.length === 0 ? (
          <div className="p-6 text-sm text-[var(--cw-muted)] text-center">
            All caught up.
          </div>
        ) : (
          open.map((t) => (
            <TaskRow
              key={t.task_id}
              t={t}
              contextDealId={contextDealId}
              onOpen={onOpen}
              onCompleteClick={onCompleteClick}
              onSnoozeClick={onSnoozeClick}
              showLinkedDeals={showLinkedDeals}
            />
          ))
        )}
        {paginated && openHasMore && !openError && (
          <div
            ref={sentinelRef}
            className="py-3 text-center text-xs text-[var(--cw-muted)]"
            data-testid="tasks-open-sentinel"
          >
            {openLoading
              ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading more…</span>
              : "Scroll to load more"}
          </div>
        )}
        {paginated && !openHasMore && open.length > 0 && (
          <div className="py-2 text-center text-[11px] text-[var(--cw-muted)]"
               data-testid="tasks-open-end">
            End of list
          </div>
        )}
      </div>

      <div>
        <button
          onClick={() => {
            setShowCompleted((v) => {
              const next = !v;
              if (next && paginated && !completedLoaded) {
                onExpandCompleted?.();
              }
              return next;
            });
          }}
          className="text-xs text-[var(--cw-muted)] inline-flex items-center gap-1 hover:text-[var(--cw-primary)]"
          data-testid="task-list-show-completed-toggle"
        >
          {showCompleted ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          Show completed ({completedN})
        </button>
        {showCompleted && (
          <div className="mt-2 cw-card overflow-hidden" data-testid="task-list-completed">
            {paginated && completedLoading && completed.length === 0 && (
              <div className="p-4"><SkeletonRows rows={3} /></div>
            )}
            {paginated && completedError && (
              <div className="p-6 text-sm text-center">
                <div className="inline-flex items-center gap-1 text-[var(--cw-error)]">
                  <AlertTriangle size={14} /> {completedError}
                </div>
              </div>
            )}
            {!completedLoading && completed.length === 0 && !completedError && (
              <div className="p-6 text-sm text-[var(--cw-muted)] text-center">
                None yet.
              </div>
            )}
            {completed.map((t) => (
              <TaskRow
                key={t.task_id}
                t={t}
                contextDealId={contextDealId}
                onOpen={onOpen}
                onCompleteClick={onCompleteClick}
                onSnoozeClick={onSnoozeClick}
                showLinkedDeals={showLinkedDeals}
              />
            ))}
            {paginated && completedHasMore && completed.length > 0 && (
              <div className="py-3 text-center">
                <button onClick={onLoadMoreCompleted}
                  disabled={completedLoading}
                  className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid="tasks-completed-load-more">
                  {completedLoading
                    ? <><Loader2 size={12} className="animate-spin" /> Loading…</>
                    : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  t,
  contextDealId,
  onOpen,
  onCompleteClick,
  onSnoozeClick,
  showLinkedDeals,
}) {
  const term = useTerm();
  const rollup = t.rollup_status || t.status || "open";
  const isCompletedHere = contextDealId
    ? (t.context_status || "open") === "completed"
    : rollup === "completed";
  const effectiveDue = t.effective_due_date || t.due_date;
  const overdue = !isCompletedHere && isPast(effectiveDue);
  const isPartial = !contextDealId && rollup === "partially_completed";

  const onRowClick = (e) => {
    if (e.target.closest("button")) return;
    onOpen?.(t);
  };

  return (
    <div
      className="px-4 py-3 border-t hover:bg-[rgba(40,42,116,0.03)] cursor-pointer"
      style={{
        borderColor: "var(--cw-border)",
        opacity: isCompletedHere ? 0.7 : 1,
      }}
      onClick={onRowClick}
      data-testid={`task-row-${t.task_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-sm truncate">{t.task_name}</div>
            <PriorityBadge p={t.priority} />
            {isPartial && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(243,191,92,0.25)", color: "#8A6510" }}
                data-testid={`task-row-partial-${t.task_id}`}
              >
                PARTIAL
              </span>
            )}
            {overdue && (
              <span
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "rgba(215,60,60,0.12)", color: "#B02B2B" }}
                data-testid={`task-row-overdue-${t.task_id}`}
              >
                OVERDUE
              </span>
            )}
            {t.reviewer_enabled && t.reviewer_name && (
              <span className="text-[10px] cw-caption uppercase">
                Reviewer: {t.reviewer_name}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-[var(--cw-muted)] flex items-center gap-3 flex-wrap">
            <span>Assigned: {t.assigned_to_name || "—"}</span>
            <span>Due: {effectiveDue || "—"}</span>
            {showLinkedDeals && (t.linked_deals || []).length > 0 && (
              <span>
                Linked to{" "}
                {(t.linked_deals || [])
                  .slice(0, 3)
                  .map((d) => d.company_name)
                  .join(", ")}
                {(t.linked_deals || []).length > 3 &&
                  ` +${t.linked_deals.length - 3} more`}
              </span>
            )}
          </div>
          {t.last_action && (
            <div className="mt-1 text-[11px] text-[var(--cw-muted)] italic">
              {actionLabel(t.last_action.event_type)} by{" "}
              {t.last_action.actor_name || "System"} · {formatDay(t.last_action.created_at)}
            </div>
          )}
        </div>
        {!isCompletedHere && (
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => onCompleteClick?.(t)}
              className="text-xs cw-btn-primary inline-flex items-center gap-1 px-2.5 py-1"
              data-testid={`task-row-complete-${t.task_id}`}
              title="Mark complete"
            >
              <CheckCircle2 size={12} /> Complete
            </button>
            <button
              onClick={() => onSnoozeClick?.(t)}
              className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border"
              style={{ borderColor: "var(--cw-border)" }}
              data-testid={`task-row-snooze-${t.task_id}`}
              title="Snooze"
            >
              <Clock size={12} /> Snooze
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PriorityBadge({ p }) {
  if (!p) return null;
  const styles = {
    high: { background: "rgba(215,60,60,0.12)", color: "#B02B2B" },
    medium: { background: "rgba(243,191,92,0.2)", color: "#8A6510" },
    low: { background: "rgba(0,0,0,0.06)", color: "var(--cw-muted)" },
  };
  return (
    <span
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
      style={styles[p] || styles.low}
    >
      {p.toUpperCase()}
    </span>
  );
}

function actionLabel(e) {
  switch (e) {
    case "created": return "Created";
    case "snoozed": return "Snoozed";
    case "completed": return "Completed";
    case "field_edited": return "Edited";
    case "deal_linked": return "Linked to a deal";
    case "deal_unlinked": return "Unlinked from a deal";
    case "followup_created": return "Follow-up created";
    case "reviewer_notified": return "Reviewer notified";
    default: return e;
  }
}

function isPast(iso) {
  if (!iso) return false;
  try {
    return new Date(iso) < new Date(new Date().toISOString().slice(0, 10));
  } catch {
    return false;
  }
}
